import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { RootState } from 'store';
import {
    MarketplacePetTag,
    MarketplacePetTags,
    MarketplaceClientTag,
    MarketplaceClientTags
} from '@spike/marketplace-model';
import { ConfirmDialog } from 'components/UI';
import { useNonInitialEffect } from '@versiondos/hooks';
import { getPetTagsThunk, getClientTagsThunk, MarketplaceStatus, TaggedContext } from '@spike/marketplace-action';
import { showError } from '@spike/notifications-action';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import Tabs, { positiveTabId, negativeTabId } from '../Tabs';
import TagTab from '../TagTab';
import styles from './EntityTags.module.scss';

export interface EntityTagsProps {
    entityType: 'pet' | 'client';
    personalities?: Array<string>;
    onSavePositivePersonality?: (personalities: Array<string>) => void;
    onSaveNegativePersonality?: (personalities: Array<string>) => void;
    className?: string;
    openSidebar?: () => void;
    isMobile?: boolean;
}

export const EntityTags: React.FC<EntityTagsProps> = ({
    entityType,
    onSavePositivePersonality,
    onSaveNegativePersonality,
    className,
    openSidebar,
    isMobile
}) => {
    const marketplace = useMarketplace();
    const loading = useSelector<RootState, boolean>(state => state.marketplace.loading);
    const status = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);
    const petStore = useSelector<RootState, MarketplacePetTags>(state => state.marketplace.petTags);
    const clientStore = useSelector<RootState, MarketplaceClientTags>(state => state.marketplace.clientTags);

    const [newTagName, setNewTagName] = useState('');
    const [editingTag, setEditingTag] = useState<string | null>(null);
    const [tagSelected, setTagSelected] = useState<MarketplacePetTag | MarketplaceClientTag | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [showModalDelete, setShowModalDelete] = useState(false);
    const [positives, setPositives] = useState<Array<MarketplacePetTag | MarketplaceClientTag>>([]);
    const [negatives, setNegatives] = useState<Array<MarketplacePetTag | MarketplaceClientTag>>([]);
    const [marketplacePositives, setMarketplacePositives] = useState<Array<string>>([]);
    const [marketplaceNegatives, setMarketplaceNegatives] = useState<Array<string>>([]);
    const [selectedTab, setSelectedTab] = useState<string>(positiveTabId);

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    // Determine which thunks and state to use based on entityType
    const getTagsThunk = entityType === 'pet' ? getPetTagsThunk : getClientTagsThunk;
    const store = entityType === 'pet' ? petStore : clientStore;
    const getPositivePersonality =
        entityType === 'pet' ? marketplace.petPositivePersonality : marketplace.clientPositivePersonality;
    const getNegativePersonality =
        entityType === 'pet' ? marketplace.petNegativePersonality : marketplace.clientNegativePersonality;
    const getSuccessStatus =
        entityType === 'pet' ? MarketplaceStatus.GetPetTagsSuccess : MarketplaceStatus.GetClientTagsSuccess;
    const entityName = entityType === 'pet' ? 'pets' : 'clients';
    const context: TaggedContext =
        entityType === 'pet'
            ? selectedTab === positiveTabId
                ? 'positive_personalities'
                : 'negative_personalities'
            : selectedTab === positiveTabId
            ? 'marketing_positive'
            : 'marketing_negative';

    useEffect(() => {
        if (store.positive.length === 0 && store.negative.length === 0) {
            dispatch(getTagsThunk(apiClientWrapper));
        } else {
            setPositives(sortedTags(store.positive));
            setNegatives(sortedTags(store.negative));
        }
    }, [entityType]);

    useEffect(() => {
        setMarketplacePositives([...getPositivePersonality]);
        setMarketplaceNegatives([...getNegativePersonality]);
    }, [getPositivePersonality, getNegativePersonality]);

    useNonInitialEffect(() => {
        if (status === MarketplaceStatus.SaveSuccess) {
            dispatch(getTagsThunk(apiClientWrapper));
            setNewTagName('');
            setShowForm(false);
            setShowModalDelete(false);
            setEditingTag('');
            setTagSelected(null);
            setMarketplacePositives(getPositivePersonality);
            setMarketplaceNegatives(getNegativePersonality);
        }

        if (status === getSuccessStatus) {
            setPositives(sortedTags(store.positive));
            setNegatives(sortedTags(store.negative));

            setMarketplacePositives(getPositivePersonality);
            setMarketplaceNegatives(getNegativePersonality);
        }
    }, [status]);

    const sortedTags = (tagList: Array<MarketplacePetTag | MarketplaceClientTag>) => {
        const getSourceOrder = (source: string) => {
            return source === 'default' ? 0 : 1;
        };

        return [...tagList].sort((a, b) => {
            const sourceOrderA = getSourceOrder(a.source);
            const sourceOrderB = getSourceOrder(b.source);

            if (sourceOrderA !== sourceOrderB) {
                return sourceOrderA - sourceOrderB;
            }

            return a.name.localeCompare(b.name);
        });
    };

    const handleAddOther = () => {
        setShowForm(true);
    };

    const handleEdit = (tag: MarketplacePetTag | MarketplaceClientTag) => {
        setNewTagName('');
        setShowForm(true);
        setNewTagName(tag.name);
        setEditingTag(tag.id);
    };

    const handleOpenModalDelete = (tag: MarketplacePetTag | MarketplaceClientTag) => {
        setShowModalDelete(true);
        setTagSelected(tag);
    };

    const handleCancel = () => {
        setShowForm(false);
        setNewTagName('');
        setEditingTag(null);
    };

    const handleCancelModal = () => {
        setShowModalDelete(false);
    };

    const handleDeleteTag = () => {
        if (!tagSelected) {
            return;
        }

        const isPositiveTab = selectedTab === positiveTabId;
        const currentList = isPositiveTab ? marketplacePositives : marketplaceNegatives;
        const onSavePersonality = isPositiveTab ? onSavePositivePersonality : onSaveNegativePersonality;

        const newTags = currentList.filter(tag => tag !== tagSelected.id);

        onSavePersonality?.(newTags);
    };

    const areFieldsValid = (name: string, tagsDefault: Array<MarketplacePetTag | MarketplaceClientTag>) => {
        const nameLower = name.toLowerCase();
        const nameWithUnderscore = nameLower.replace(/'/g, '').replace(/ /g, '_');
        const nameWithHyphen = nameLower.replace(/'/g, '').replace(/ /g, '-');

        const tagExists = tagsDefault.some(tag => {
            const tagIdLower = tag.id.toLowerCase();
            return tagIdLower === nameWithUnderscore || tagIdLower === nameWithHyphen;
        });

        return tagExists;
    };

    const cleanInput = (value: string): string => {
        return value.replace(/[^a-zA-Z0-9\s]/g, '');
    };

    const handleAddTag = (name: string, editingTag: string | null) => {
        let newValue = name;

        newValue = cleanInput(newValue);
        setNewTagName(newValue);

        if (newValue.trim() === '') {
            return;
        }

        const isPositiveTab = selectedTab === positiveTabId;
        const currentList = isPositiveTab ? marketplacePositives : marketplaceNegatives;
        const currentListAll = isPositiveTab ? positives : negatives;
        const onSavePersonality = isPositiveTab ? onSavePositivePersonality : onSaveNegativePersonality;

        if (areFieldsValid(newValue, currentListAll)) {
            dispatch(showError('This tag is already added'));
            return;
        }

        let newTags: string[];

        if (editingTag) {
            newTags = currentList.filter(tag => tag !== editingTag).concat(newValue);
        } else {
            newTags = [...currentList, newValue];
        }

        onSavePersonality?.(newTags);
    };

    const handleSelectTab = (tabId: string) => {
        setSelectedTab(tabId);
        setShowForm(false);
        setNewTagName('');
        setEditingTag(null);
    };

    const personalitiesToShow = selectedTab === positiveTabId ? positives : negatives;
    const activeTabId = selectedTab === positiveTabId ? positiveTabId : negativeTabId;

    return (
        <div className={clsx(styles.container, className)}>
            <Tabs
                tabId={selectedTab}
                selectTabHandler={handleSelectTab}
            />
            {(selectedTab === positiveTabId || selectedTab === negativeTabId) && (
                <TagTab
                    tabId={activeTabId}
                    loading={loading}
                    showForm={showForm}
                    newTagName={newTagName}
                    editingTag={editingTag}
                    personalities={personalitiesToShow}
                    entityType={entityType}
                    context={context}
                    setNewTagName={setNewTagName}
                    openModalDelete={handleOpenModalDelete}
                    addOtherHandle={handleAddOther}
                    addPetTagHandle={handleAddTag}
                    editPetTagHandle={handleEdit}
                    addCancelHandle={handleCancel}
                />
            )}
            {showModalDelete && tagSelected && (
                <ConfirmDialog
                    open={true}
                    title={<span>Delete {tagSelected.name}</span>}
                    question={
                        <span>
                            This tag is assigned to <strong>{tagSelected.count}</strong> {entityName}. <br />
                            Are you sure you want to delete it?
                        </span>
                    }
                    cancelButtonLabel="Cancel"
                    confirmButtonLabel="Delete"
                    onCancel={handleCancelModal}
                    onConfirm={handleDeleteTag}
                    processing={loading}
                />
            )}
        </div>
    );
};

export default EntityTags;
